// src/Dashboard/pages/user/Messaging/components/ChatChannelsPanel.jsx

import React, { useMemo, useState } from "react";
import { FaPaperPlane, FaSyncAlt, FaUsers } from "react-icons/fa";

import ChatMessageBubble from "./ChatMessageBubble";

import { CHAT_BG_THEMES } from "../utils/messagingUtils";

export default function ChatChannelsPanel({
  isManager,
  userId,
  channels,
  selectedChannel,
  selectedChannelId,
  setSelectedChannelId,
  loadingChannels,
  messages,
  loadingMessages,
  sending,
  onSend,
  onDelete,
  onRefresh,
}) {
  const [draft, setDraft] = useState("");
  const [theme, setTheme] = useState("slate");

  const themeClass = useMemo(() => {
    return CHAT_BG_THEMES.find((item) => item.id === theme)?.area || CHAT_BG_THEMES[0].area;
  }, [theme]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const sent = await onSend?.(draft);

    if (sent) {
      setDraft("");
    }
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Canaux de discussion
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Discussions de groupes
            </h2>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {isManager
                ? "Vous pouvez consulter les canaux de tous les groupes."
                : "Vous voyez uniquement les canaux liés à vos adhérents."}
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={theme}
              onChange={(event) => setTheme(event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              {CHAT_BG_THEMES.map((item) => (
                <option key={item.id} value={item.id}>
                  Fond : {item.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => onRefresh?.()}
              disabled={!selectedChannelId || loadingMessages}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              <FaSyncAlt className={loadingMessages ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      <div className="grid min-h-[680px] lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950 lg:border-b-0 lg:border-r">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
            <FaUsers />
            Canaux accessibles
          </div>

          {loadingChannels ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="h-12 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : channels.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              Aucun canal disponible.
            </div>
          ) : (
            <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1 lg:max-h-[610px]">
              {channels.map((channel) => {
                const active = Number(channel.id) === Number(selectedChannelId);

                return (
                  <button
                    key={channel.id}
                    type="button"
                    onClick={() => setSelectedChannelId(channel.id)}
                    className={`w-full rounded-2xl px-4 py-3 text-left transition ${
                      active
                        ? "bg-[#013E8E] text-white shadow-sm"
                        : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-800 dark:hover:bg-slate-800"
                    }`}
                  >
                    <span className="block text-sm font-bold">
                      {channel.nom}
                    </span>

                    {channel.adherentNom && (
                      <span
                        className={`mt-1 block text-xs ${
                          active ? "text-white/75" : "text-slate-500"
                        }`}
                      >
                        {channel.adherentNom}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <div className="flex min-w-0 flex-col">
          <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="font-bold text-slate-900 dark:text-white">
              Canal actuel : {selectedChannel?.nom || "—"}
            </h3>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Rappel : respect, pas de données personnelles sensibles, langage
              correct et esprit club.
            </p>
          </div>

          <div
            className={`flex-1 overflow-y-auto px-3 py-4 sm:px-5 ${themeClass}`}
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {loadingMessages ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-16 animate-pulse rounded-2xl bg-white/70 dark:bg-slate-800/70"
                  />
                ))}
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full min-h-[360px] items-center justify-center">
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-white/80 px-5 py-10 text-center dark:border-slate-700 dark:bg-slate-900/70">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Aucun message
                  </h3>

                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                    Démarrez la conversation dans ce canal.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => (
                  <ChatMessageBubble
                    key={message.id}
                    message={message}
                    canDelete={
                      Number(userId) === Number(message.user_id) || isManager
                    }
                    onDelete={() => onDelete?.(message.id)}
                  />
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95 sm:p-4"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                disabled={!selectedChannelId || sending}
                placeholder={
                  selectedChannelId
                    ? "Écrire un message dans le canal..."
                    : "Sélectionnez un canal"
                }
                className="min-w-0 flex-1 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />

              <button
                type="submit"
                disabled={!selectedChannelId || sending || !draft.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaPaperPlane />
                <span className="hidden sm:inline">
                  {sending ? "Envoi..." : "Envoyer"}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}